import type {
  ProfileCompletionSectionId,
  ProfileCompletionSummary,
} from '@/lib/profileCompletion';

export const PROFILE_ONBOARDING_POLICY_VERSION = 1;
export const PROFILE_ONBOARDING_MAX_DEFERRALS = 2;

export type ProfileOnboardingGateState =
  | 'ALLOW'
  | 'REQUIRE_PERSONAL'
  | 'DEFERABLE'
  | 'REQUIRED';

interface ProfileOnboardingDecisionInput {
  summary: ProfileCompletionSummary;
  deferralCount: number;
  deferredInCurrentSession: boolean;
}

export interface ProfileOnboardingDecision {
  state: ProfileOnboardingGateState;
  section: ProfileCompletionSectionId | null;
  remainingDeferrals: number;
}

export function getProfileOnboardingDecision({
  summary,
  deferralCount,
  deferredInCurrentSession,
}: ProfileOnboardingDecisionInput): ProfileOnboardingDecision {
  const personalComplete = summary.sections.find((section) => section.id === 'personal')?.complete;
  const boundedDeferrals = Math.max(
    0,
    Math.min(PROFILE_ONBOARDING_MAX_DEFERRALS, deferralCount),
  );

  if (!personalComplete) {
    return {
      state: 'REQUIRE_PERSONAL',
      section: 'personal',
      remainingDeferrals: PROFILE_ONBOARDING_MAX_DEFERRALS - boundedDeferrals,
    };
  }

  if (summary.complete || deferredInCurrentSession) {
    return {
      state: 'ALLOW',
      section: null,
      remainingDeferrals: PROFILE_ONBOARDING_MAX_DEFERRALS - boundedDeferrals,
    };
  }

  return {
    state: boundedDeferrals >= PROFILE_ONBOARDING_MAX_DEFERRALS ? 'REQUIRED' : 'DEFERABLE',
    section: summary.firstIncompleteSection,
    remainingDeferrals: PROFILE_ONBOARDING_MAX_DEFERRALS - boundedDeferrals,
  };
}

/**
 * Setup a Business Owner may do before their own details are complete. Everything
 * that records time, pay, documents or approvals stays behind the gate.
 */
export const OWNER_SETUP_PATHS = [
  '/bo/dashboard',
  '/bo/employees',
  '/bo/managers',
  '/bo/clients',
  '/bo/jobs',
  '/bo/settings',
] as const;

export function isOwnerSetupPath(pathname: string) {
  return OWNER_SETUP_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

export function getOnboardingSectionPath(section: ProfileCompletionSectionId | null) {
  if (section === 'availability') return '/employee/availability?setup=1';
  return `/profile?setup=profile-required&section=${section ?? 'personal'}`;
}

export function getJwtSessionId(accessToken: string | null | undefined) {
  if (!accessToken) return null;
  const payload = accessToken.split('.')[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const claims = JSON.parse(atob(padded)) as { session_id?: unknown };
    return typeof claims.session_id === 'string' ? claims.session_id : null;
  } catch {
    return null;
  }
}
